import React, { useRef, useState, useEffect } from 'react'
import Map from './components/Map'
import MapUI from './components/MapUI'
import { useNotificationHandler } from 'hooks/useNotificationHandler';
import { notification } from 'antd';
import dayjs from "dayjs";

import LayersAndResolutions from 'data/layers/LayersAndResolutions.json';

import { getDateAsStringArray, sortByDateAsc } from 'utils/dateFunctions';
import MapLeftBar from './components/MapLeftBar';
import useActiveLayersStore from 'stores/useSelectedLayersStore';
import useLeafletLayerManager from 'hooks/useLeafletLayerManager';
import fileRecognize from 'utils/fileType';
import useSearchParamsManager from 'hooks/useSearchParamsManager';

const MapSection = ({
    startDate, setStartDate, mapParameters, setMapParameters, markerModeRef, setTscCoords,
    enabledDates, tscModeRef, isButtonClicked, onClick, mapRef, predictionDayProps, setEnabledDates,
    fetchCommonDates, setZSTPopUpContent, layerRefs, isDatePickerDisabled, setIsDatePickerDisabled
}) => {

    const selectedLayers = useActiveLayersStore((state) => state.selectedLayers);

    const getSelectedLayerInfo = useActiveLayersStore((state) => state.getSelectedLayerInfo);
    const addSelectedLayer = useActiveLayersStore((state) => state.addSelectedLayer);
    const updateSelectedLayers = useActiveLayersStore((state) => state.updateSelectedLayers);
    const clearSelectedLayers = useActiveLayersStore((state) => state.clearSelectedLayers);
    const deleteSelectedLayerByIndex = useActiveLayersStore((state) => state.deleteSelectedLayerByIndex);
    const searchSelectedLayerIndex = useActiveLayersStore((state) => state.searchSelectedLayerIndex);

    const { addLeafletLayer, createLeafletLayer, updateLeafletLayer, removeLeafletLayer } = useLeafletLayerManager();
    const { setStartDateUrlParam, setNewLayerUrlParam, removeLayerUrlParam, updateLayerUrlParam, clearLayerUrlParams, retrieveDataFromUrl } = useSearchParamsManager();

    
    const [opacities, setOpacities] = useState([]);
    

    const { openNotification, handleError, contextHolder } = useNotificationHandler();

    useEffect(() => {

        let isCancelled = false; // This variable prevents errors if it's activated.

        const setStateFromUrl = async () => {

            const result = retrieveDataFromUrl();
            if (!result) return;

            const { newSelectedLayers, newSpatialResolutions, newTemporalResolutions, parsedDate } = result;

            const newMapParameters = newSelectedLayers.map((_, i) => {
                return fileRecognize(newSelectedLayers[i], newSpatialResolutions[i], newTemporalResolutions[i], 0);
            });
            const commonDates = await fetchCommonDates(newMapParameters);
            if(isCancelled || commonDates == 0) return;

            updateSelectedLayers(newSpatialResolutions, newTemporalResolutions, newSelectedLayers);
            setEnabledDates(commonDates);
            setStartDate(parsedDate);
            setIsDatePickerDisabled(false);
            setMapParameters(newMapParameters);

            let { selectedDay, selectedMonth, selectedYear } = getDateAsStringArray(parsedDate);
            const newOpacities = newMapParameters.map(() => 50);
            newMapParameters.forEach(async (layerParameters) => {
                const newLayer = await createLeafletLayer(layerParameters, selectedDay, selectedMonth, selectedYear, 50, openNotification, handleError);
                addLeafletLayer(newLayer, mapRef, layerRefs);
            });
            setOpacities(newOpacities);

        };

        setStateFromUrl();

        return(() => {
            isCancelled = true;
        });

    }, []);

    const showLayer = async (key) => {
        if (!(selectedLayers.includes(key))) {

            const layer = LayersAndResolutions.layers.find((layer) => layer.longName === key);
            const layerParameters = fileRecognize(layer.longName, layer.spaceResolution[0], layer.timeResolution[0], predictionDayProps.predictionDay);

            const newMapParameters = [...mapParameters, layerParameters];
            const commonDates = await fetchCommonDates(newMapParameters);

            if (commonDates.length !== 0) {

                // Add layer to states.
                addSelectedLayer(layer.spaceResolution[0], layer.timeResolution[0], key);
                setMapParameters(newMapParameters);
                setNewLayerUrlParam(layerParameters);

                const orderedDates = sortByDateAsc(commonDates);
                setEnabledDates(orderedDates); // Set the common dates in the state

                const newStartDate = findCommonDate(orderedDates);
                setStartDate(newStartDate);
                setIsDatePickerDisabled(false);
                setStartDateUrlParam(newStartDate);

                let { selectedDay, selectedMonth, selectedYear } = getDateAsStringArray(newStartDate);
                const newOpacities = [...opacities, 50];
                setOpacities(newOpacities);
                const newLayer = await createLeafletLayer(layerParameters, selectedDay, selectedMonth, selectedYear, 50, openNotification, handleError);
                addLeafletLayer(newLayer, mapRef, layerRefs);

                setZSTPopUpContent(null); // We also reset the ZST content.

            } else { // The last added layer has no coinciding dates, so it must be removed.

                notification.warning({
                    message: 'No matching dates',
                    description: 'There are no matching dates between the layers you have selected.',
                    placement: 'top',
                    style: { backgroundColor: '#fff', color: '#000', borderRadius: '6px' },
                });

            };

        };
    };

    /* ### SHOULD BE MOVED TO THE DATE UTILS? ### */
    const findCommonDate = (dates) => {

        if (startDate) {
            if(dates.includes(startDate.format("YYYY-MM-DD"))) return startDate;
            else return dayjs(dates[dates.length - 1]);
        } else {
            return dayjs(dates[dates.length - 1]);
        }

    };

    const handleNoLayers = () => {
        setStartDate(null);
        setEnabledDates([]);
        setIsDatePickerDisabled(true);
        setMapParameters([]);
        clearSelectedLayers();
        clearLayerUrlParams();
        setOpacities([]);
    };

    const hideLayer = async (layer) => {

        const index = searchSelectedLayerIndex(layer);
        removeLeafletLayer(index, mapRef, layerRefs);

        // Restart remaining states, or handle the MapParameters and dates.
        if (layerRefs.current.length == 0) handleNoLayers();
        else {
            deleteSelectedLayerByIndex(index);
            removeLayerUrlParam(mapParameters[index]);
            setOpacities((prev) => prev.filter((_, i) => i != index));

            const newMapParameters = mapParameters.filter((_, i) => i != index);
            setMapParameters(newMapParameters);
            await fetchCommonDates(newMapParameters);
        }

    }

    const updateLayer = async (layerName, index) => {
        const { selectedDay, selectedMonth, selectedYear } = getDateAsStringArray(startDate);
        const { spaceRes, temporalRes } = getSelectedLayerInfo(index);

        // We get the layer parameters to create the layer.
        const layerParameters = fileRecognize(layerName, spaceRes, temporalRes, predictionDayProps.predictionDay);
        updateLayerUrlParam(mapParameters[index], layerParameters);
        setMapParameters((prev) => prev.map((value, i) => i === index ? layerParameters : value));

        // We create and update the layer.
        const updatedLayer = await createLeafletLayer(layerParameters, selectedDay, selectedMonth, selectedYear, opacities[index], openNotification, handleError);
        updateLeafletLayer(updatedLayer, index, mapRef, layerRefs);
    };

    return (
        <div id="map-container">
            {contextHolder}
            <MapLeftBar
                mapRef={mapRef}
                updateLayer={updateLayer}
                showLayer={showLayer}
                hideLayer={hideLayer}
            />

            <div id="map-section">
                <Map
                    markerModeRef={markerModeRef}
                    tscModeRef={tscModeRef}
                    setTscCoords={setTscCoords}
                    mapRef={mapRef}
                />

                <MapUI
                    startDate={startDate}
                    enabledDates={enabledDates}
                    setStartDate={setStartDate}
                    isButtonClicked={isButtonClicked}
                    onClick={onClick}
                    mapRef={mapRef}
                    mapParameters={mapParameters}
                    setMapParameters={setMapParameters}
                    layerRefs={layerRefs}
                    opacities={opacities}
                    setOpacities={setOpacities}
                    predictionDayProps={predictionDayProps}
                    isDatePickerDisabled={isDatePickerDisabled}
                />

            </div>

        </div>
    );

}

export default MapSection;